import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Box,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: 500,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  '& .MuiTableCell-root': {
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: '8px 12px',
    '&:last-child': {
      borderRight: 'none'
    }
  }
}));

const CategoryCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#2a2a2a',
  position: 'sticky',
  left: 0,
  zIndex: 2,
  minWidth: 150,
  '&.MuiTableCell-head': {
    zIndex: 3
  }
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#2a2a2a',
  textAlign: 'center',
  minWidth: 120,
  maxWidth: 150,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  position: 'sticky',
  top: 0,
  zIndex: 2
}));

const CheckboxCell = styled(TableCell)({
  textAlign: 'center',
  padding: '4px'
});

interface TabItem {
  _id: string;
  name: string;
  code: string;
}

interface TabsPermissionMatrixProps {
  tabs: TabItem[];
  selectedTabs: string[];
  onChange: (selectedTabs: string[]) => void;
}

interface CategoryData {
  label: string;
  pages: {
    label: string;
    code: string;
  }[];
}

const TabsPermissionMatrix: React.FC<TabsPermissionMatrixProps> = ({
  tabs,
  selectedTabs,
  onChange
}) => {
  const categories: CategoryData[] = useMemo(() => [
    {
      label: 'Управління',
      pages: [
        { label: 'Карта', code: 'control' },
        { label: 'Самокати', code: 'scooters' },
        { label: 'Поїздки', code: 'contracts' },
        { label: 'Повідомлення', code: 'notices' },
        { label: 'Історія блок/розблок', code: 'scooters-lock-history' },
        { label: 'Користувачі', code: 'users' },
        { label: 'Відгуки', code: 'contractReviews' },
        { label: 'Trackit', code: 'trackit' },
        { label: 'Загублені самокати', code: 'lost-scooters' }
      ]
    },
    {
      label: 'Маркетинг',
      pages: [
        { label: 'Промокоди', code: 'bonuscodes' },
        { label: 'Абонементи', code: 'tariffs' },
        { label: 'Винагороди', code: 'rewards' },
        { label: 'Пуш-повідомлення', code: 'pushes' },
        { label: 'Новини', code: 'news' }
      ]
    },
    {
      label: 'Сервіс',
      pages: [
        { label: 'Нотатки', code: 'scooter_notes' },
        { label: 'Склад запчастин', code: 'spare_parts' },
        { label: 'Задачі майстерні', code: 'workshop_tasks' }
      ]
    },
    {
      label: 'Адміністрування',
      pages: [
        { label: 'Транзакції', code: 'history' }
      ]
    },
    {
      label: 'Аналітика',
      pages: [
        { label: 'Статистика', code: 'statistics' }
      ]
    },
    {
      label: 'Налаштування',
      pages: [
        { label: 'Тарифи', code: 'tariffs' },
        { label: 'Робочі години', code: 'working_hours' },
        { label: 'Зони (редагування)', code: 'zone-edit' },
        { label: 'Маркери на карті', code: 'map_markers' },
        { label: 'Типи самокатів', code: 'scooter_types' },
        { label: 'FAQ', code: 'faq' }
      ]
    }
  ], []);

  const tabsMap = useMemo(() => {
    const map = new Map<string, string>();
    tabs.forEach(tab => {
      map.set(tab.code, tab._id);
    });
    return map;
  }, [tabs]);

  const allDefinedCodes = useMemo(() => {
    return new Set(categories.flatMap(cat => cat.pages.map(p => p.code)));
  }, [categories]);

  const categoriesWithOther = useMemo(() => {
    const otherTabs = tabs.filter(tab => !allDefinedCodes.has(tab.code));
    
    if (otherTabs.length === 0) {
      return categories;
    }

    return [
      ...categories,
      {
        label: 'Інше',
        pages: otherTabs.map(tab => ({
          label: tab.name,
          code: tab.code
        }))
      }
    ];
  }, [categories, tabs, allDefinedCodes]);

  const allPageCodes = useMemo(() => {
    return categoriesWithOther.flatMap(cat => cat.pages.map(p => p.code));
  }, [categoriesWithOther]);

  const handleToggle = (code: string) => {
    const tabId = tabsMap.get(code);
    if (!tabId) return;

    const isSelected = selectedTabs.includes(tabId);
    const newSelectedTabs = isSelected
      ? selectedTabs.filter(id => id !== tabId)
      : [...selectedTabs, tabId];
    
    onChange(newSelectedTabs);
  };

  const handleSelectAllInCategory = (category: CategoryData) => {
    const categoryTabIds = category.pages
      .map(p => tabsMap.get(p.code))
      .filter((id): id is string => id !== undefined);
    
    const allSelected = categoryTabIds.every(id => selectedTabs.includes(id));
    
    if (allSelected) {
      onChange(selectedTabs.filter(id => !categoryTabIds.includes(id)));
    } else {
      const newSelectedTabs = [...selectedTabs];
      categoryTabIds.forEach(id => {
        if (!newSelectedTabs.includes(id)) {
          newSelectedTabs.push(id);
        }
      });
      onChange(newSelectedTabs);
    }
  };

  const handleSelectAll = () => {
    const allTabIds = allPageCodes
      .map(code => tabsMap.get(code))
      .filter((id): id is string => id !== undefined);
    
    const allSelected = allTabIds.length > 0 && allTabIds.every(id => selectedTabs.includes(id));
    
    if (allSelected) {
      onChange([]);
    } else {
      onChange(allTabIds);
    }
  };

  const isCategorySelected = (category: CategoryData) => {
    const categoryTabIds = category.pages
      .map(p => tabsMap.get(p.code))
      .filter((id): id is string => id !== undefined);
    
    if (categoryTabIds.length === 0) return false;
    return categoryTabIds.every(id => selectedTabs.includes(id));
  };

  const isCategoryPartiallySelected = (category: CategoryData) => {
    const categoryTabIds = category.pages
      .map(p => tabsMap.get(p.code))
      .filter((id): id is string => id !== undefined);
    
    if (categoryTabIds.length === 0) return false;
    const selectedCount = categoryTabIds.filter(id => selectedTabs.includes(id)).length;
    return selectedCount > 0 && selectedCount < categoryTabIds.length;
  };

  const allSelected = useMemo(() => {
    const allTabIds = allPageCodes
      .map(code => tabsMap.get(code))
      .filter((id): id is string => id !== undefined);
    return allTabIds.length > 0 && allTabIds.every(id => selectedTabs.includes(id));
  }, [allPageCodes, selectedTabs, tabsMap]);

  const someSelected = useMemo(() => {
    const allTabIds = allPageCodes
      .map(code => tabsMap.get(code))
      .filter((id): id is string => id !== undefined);
    const selectedCount = allTabIds.filter(id => selectedTabs.includes(id)).length;
    return selectedCount > 0 && selectedCount < allTabIds.length;
  }, [allPageCodes, selectedTabs, tabsMap]);

  const maxPagesInCategory = Math.max(...categoriesWithOther.map(cat => cat.pages.length));

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Дозволи доступу
      </Typography>
      <StyledTableContainer>
        <Table size="small">
          <TableBody>
            {categoriesWithOther.map((category, catIndex) => (
              <TableRow key={catIndex} hover>
                <CategoryCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={isCategorySelected(category)}
                      indeterminate={isCategoryPartiallySelected(category)}
                      onChange={() => handleSelectAllInCategory(category)}
                      size="small"
                    />
                    {category.label}
                  </Box>
                </CategoryCell>
                {Array.from({ length: maxPagesInCategory }).map((_, pageIndex) => {
                  const page = category.pages[pageIndex];
                  if (!page) {
                    return <CheckboxCell key={pageIndex} />;
                  }
                  
                  const tabId = tabsMap.get(page.code);
                  const isChecked = tabId ? selectedTabs.includes(tabId) : false;
                  const tabExists = tabsMap.has(page.code);
                  
                  return (
                    <CheckboxCell key={pageIndex}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2, textAlign: 'center' }}>
                          {page.label}
                        </Typography>
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleToggle(page.code)}
                          size="small"
                          disabled={!tabExists}
                        />
                      </Box>
                    </CheckboxCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Вибрано: {selectedTabs.length} з {tabs.length} доступних табів
      </Typography>
    </Box>
  );
};

export default TabsPermissionMatrix;
